"use client";

import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FileText, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { HELP_CENTER_PATH, KB_BASE_PATH } from "../paths";

const MAX_RESULTS = 8;
const SNIPPET_LENGTH = 110;

// Words too common to narrow a search down. They are still used when a query
// is made of nothing else.
const STOP_WORDS = new Set(
  "a an and are as at be by can do does for from how i in is it my of on or the to what when where with you your".split(
    " ",
  ),
);

// The index is only fetched once someone starts searching, and shared by
// every search box on the page.
let indexPromise = null;
function loadIndex() {
  indexPromise ??= fetch(`${HELP_CENTER_PATH}/search-index`)
    .then((response) => {
      if (!response.ok) throw new Error("Search index unavailable");
      return response.json();
    })
    .catch((error) => {
      indexPromise = null;
      throw error;
    });
  return indexPromise;
}

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Query words, with a plural "s" dropped so "appointments" also finds
// "appointment". Each term matches the start of a word.
function queryTerms(query) {
  const words = query.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const meaningful = words.filter((word) => !STOP_WORDS.has(word));
  return [...new Set(meaningful.length ? meaningful : words)].map((word) =>
    word.length > 3 && word.endsWith("s") && !word.endsWith("ss")
      ? word.slice(0, -1)
      : word,
  );
}

const termRegExp = (term) => new RegExp(`\\b${escapeRegExp(term)}`, "i");

// Every term has to appear somewhere in the article; matches in the title
// count most, then the summary, category and body.
function search(index, query) {
  const terms = queryTerms(query);
  if (!terms.length) return [];
  const patterns = terms.map(termRegExp);
  const phrase = query.trim().toLowerCase();

  return index
    .map((entry) => {
      let score = 0;
      for (const pattern of patterns) {
        const inTitle = pattern.test(entry.title);
        const inSummary = pattern.test(entry.summary);
        const inCategory = pattern.test(entry.category);
        const inBody = pattern.test(entry.body);
        if (!inTitle && !inSummary && !inCategory && !inBody) return null;
        score +=
          (inTitle ? 10 : 0) +
          (inSummary ? 4 : 0) +
          (inCategory ? 3 : 0) +
          (inBody ? 1 : 0);
      }
      if (entry.title.toLowerCase().includes(phrase)) score += 20;
      return { entry, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map(({ entry }) => ({ ...entry, snippet: snippet(entry, patterns) }));
}

// The summary when it matches, otherwise the part of the article around the
// first match.
function snippet(entry, patterns) {
  if (patterns.some((pattern) => pattern.test(entry.summary))) {
    return entry.summary;
  }
  const positions = patterns
    .map((pattern) => entry.body.search(pattern))
    .filter((position) => position >= 0);
  if (!positions.length) return entry.summary;

  // Start a little before the match, at the beginning of a word.
  let start = Math.max(0, Math.min(...positions) - 30);
  if (start > 0) start = entry.body.indexOf(" ", start) + 1;
  const text = entry.body.slice(start, start + SNIPPET_LENGTH).trim();
  return `${start > 0 ? "…" : ""}${text}${
    start + SNIPPET_LENGTH < entry.body.length ? "…" : ""
  }`;
}

function Highlight({ text, terms }) {
  if (!terms.length) return text;
  const pattern = new RegExp(
    `(\\b(?:${terms.map(escapeRegExp).join("|")})[a-z0-9]*)`,
    "gi",
  );
  return text.split(pattern).map((part, index) =>
    index % 2 ? (
      <mark key={index} className="rounded-sm bg-fe-primary/30 text-inherit">
        {part}
      </mark>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

// Search box for the help center articles, with the matches listed in a
// dropdown underneath. `size` is "lg" for the help center home hero and "sm"
// for the top bar.
export function HelpSearch({ size = "sm", className }) {
  const router = useRouter();
  const listId = useId();
  const containerRef = useRef(null);
  const [index, setIndex] = useState(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const isSearching = query.trim().length >= 2;
  const terms = useMemo(() => queryTerms(query), [query]);
  const results = useMemo(
    () => (index && isSearching ? search(index, query) : []),
    [index, isSearching, query],
  );

  const ensureIndex = () => {
    if (index) return;
    setFailed(false);
    loadIndex()
      .then(setIndex)
      .catch(() => setFailed(true));
  };

  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const goTo = (href) => {
    setOpen(false);
    router.push(href);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (current) => (current - 1 + results.length) % results.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      goTo(results[activeIndex]?.href ?? results[0].href);
    }
  };

  const showDropdown = open && isSearching;
  const isLarge = size === "lg";

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div
        className={cn(
          "flex items-center gap-3 rounded-full border bg-white transition-shadow focus-within:border-neutral-400",
          isLarge
            ? "h-14 border-neutral-300 px-6 focus-within:shadow-sm md:h-16"
            : "h-11 border-neutral-200 px-4",
        )}
      >
        <Search
          className={cn(
            "shrink-0 text-neutral-500",
            isLarge ? "size-5" : "size-4",
          )}
        />
        <input
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label="Search the help center"
          placeholder="Search the help center"
          value={query}
          onFocus={() => {
            ensureIndex();
            setOpen(true);
          }}
          onChange={(event) => {
            ensureIndex();
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full min-w-0 bg-transparent text-neutral-900 outline-none placeholder:text-neutral-500 [&::-webkit-search-cancel-button]:hidden",
            isLarge ? "text-base md:text-lg" : "text-sm",
          )}
        />
      </div>

      {showDropdown && (
        <div
          className={cn(
            "absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-neutral-200 bg-white text-left shadow-[0_8px_50px_#80808026]",
            !isLarge && "md:left-auto md:w-[28rem]",
          )}
        >
          {results.length > 0 ? (
            <>
              <p className="px-5 pt-4 pb-1 text-sm font-medium text-neutral-500">
                Results
              </p>
              <ul
                id={listId}
                role="listbox"
                className="max-h-[60vh] overflow-y-auto px-2 pb-2"
              >
                {results.map((result, resultIndex) => (
                  <li
                    key={result.href}
                    role="option"
                    aria-selected={resultIndex === activeIndex}
                  >
                    <Link
                      href={result.href}
                      onClick={() => setOpen(false)}
                      onMouseEnter={() => setActiveIndex(resultIndex)}
                      className={cn(
                        "flex gap-3 rounded-xl px-3 py-3",
                        resultIndex === activeIndex && "bg-neutral-100",
                      )}
                    >
                      <FileText className="mt-0.5 size-4 shrink-0 text-neutral-500" />
                      <span className="min-w-0">
                        <span className="block font-medium text-neutral-900">
                          <Highlight text={result.title} terms={terms} />
                        </span>
                        <span className="mt-0.5 line-clamp-2 block text-sm text-neutral-600">
                          <Highlight text={result.snippet} terms={terms} />
                        </span>
                        <span className="mt-1 block text-xs text-neutral-500">
                          {result.category}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="px-5 py-5 text-sm text-neutral-600">
              {failed ? (
                "Search isn't available right now. Please try again."
              ) : !index ? (
                "Searching…"
              ) : (
                <>
                  <p className="font-medium text-neutral-900">
                    No articles match &ldquo;{query.trim()}&rdquo;
                  </p>
                  <p className="mt-1">
                    Try different words, browse the{" "}
                    <Link
                      href={KB_BASE_PATH}
                      className="font-medium text-neutral-900 underline underline-offset-4"
                    >
                      knowledge base
                    </Link>{" "}
                    or{" "}
                    <Link
                      href="/contact"
                      className="font-medium text-neutral-900 underline underline-offset-4"
                    >
                      contact us
                    </Link>
                    .
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
